import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useScreenSize } from '../../hooks/useScreensize'
import { useConstant } from '../../hooks/useConstant'
import { AppendMobileSearch } from '../AppendMobileSearch'
import { SearchScope } from '../../../types/searchScope'
import { github } from '../../utils/externalLinks'
import { Typography } from '@oasisprotocol/ui-library/src/components/typography'
import { Link } from '@oasisprotocol/ui-library/src/components/link'
import { cn } from '@oasisprotocol/ui-library/src/lib/utils'

interface FooterProps {
  scope?: SearchScope
  mobileSearchAction?: ReactNode
  enableMobileSearch?: boolean
}

const FooterLinks: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()
  const sha = (import.meta.env.REACT_APP_BUILD_SHA || '').substring(0, 7)
  const version = import.meta.env.REACT_APP_BUILD_VERSION || 'v1.0.0'
  const privacyUrl = import.meta.env.REACT_APP_PRIVACY_POLICY
  const currentYear = useConstant(() => new Date().getFullYear())

  return (
    <div className={cn('flex w-full flex-col gap-1 sm:flex-row sm:items-center sm:justify-between', className)}>
      <Typography variant="xsmall" textColor="muted" className="flex flex-wrap items-center gap-x-1">
        <span>
          {t('footer.versionLabel')}{' '}
          {import.meta.env.REACT_APP_BUILD_VERSION ? (
            <Link
              href={`${github.releaseTag}${version}`}
              rel="noopener noreferrer"
              target="_blank"
              textColor="inherit"
              className="underline"
            >
              {version}
            </Link>
          ) : (
            <span>{version}</span>
          )}{' '}
          (
          {t('footer.commitLabel')}{' '}
          {sha ? (
            <Link
              href={`${github.commit}${import.meta.env.REACT_APP_BUILD_SHA}`}
              rel="noopener noreferrer"
              target="_blank"
              textColor="inherit"
              className="underline"
            >
              {sha}
            </Link>
          ) : (
            <span>-</span>
          )}
          )
        </span>
        <span>|</span>
        <Link href={github.home} rel="noopener noreferrer" target="_blank" textColor="inherit" className="underline">
          {t('footer.github')}
        </Link>
      </Typography>

      <Typography variant="xsmall" textColor="muted" className="flex flex-wrap items-center gap-x-1">
        <span>
          © {currentYear} {t('footer.title')}
        </span>
        {privacyUrl ? (
          <>
            <span>|</span>
            <Link href={privacyUrl} rel="noopener noreferrer" target="_blank" textColor="inherit" className="underline">
              {t('analyticsConsent.privacyPolicy')}
            </Link>
          </>
        ) : null}
      </Typography>
    </div>
  )
}

export const Footer: FC<FooterProps> = ({ scope, mobileSearchAction, enableMobileSearch = true }) => {
  const { isTablet } = useScreenSize()

  return (
    <footer>
      <div
        className={cn('flex justify-between items-center py-6 sm:px-6', enableMobileSearch ? 'px-2' : 'px-4')}
      >
        {isTablet ? (
          <AppendMobileSearch
            scope={scope}
            action={mobileSearchAction}
            enableMobileSearch={enableMobileSearch}
          >
            <FooterLinks />
          </AppendMobileSearch>
        ) : (
          <FooterLinks />
        )}
      </div>
    </footer>
  )
}
